import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Loader2, Copy, Lock, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function TemplateShare() {
    const { templateId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [copying, setCopying] = useState(false);
    const [template, setTemplate] = useState(null);
    const [user, setUser] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
            // Redirect to login with return URL
            const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
            navigate(`/login?redirect=${returnUrl}`);
            return;
        }

        setUser(user);
        await fetchTemplate();
    };

    const fetchTemplate = async () => {
        try {
            setLoading(true);

            // Get share token from URL
            const urlParams = new URLSearchParams(window.location.search);
            const shareToken = urlParams.get('token');

            if (!shareToken) {
                setError('Invalid share link - missing token');
                setLoading(false);
                return;
            }

            // Fetch template with share token validation
            const { data, error } = await supabase
                .from('templates')
                .select('*')
                .eq('id', templateId)
                .eq('share_token', shareToken)
                .eq('share_enabled', true)
                .single();

            if (error || !data) {
                setError('Template not found or share link has expired');
                setLoading(false);
                return;
            }

            // Check if share link is expired
            if (data.share_expires_at && new Date(data.share_expires_at) < new Date()) {
                setError('This share link has expired');
                setLoading(false);
                return;
            }

            setTemplate(data);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching template:', err);
            setError('Failed to load template');
            setLoading(false);
        }
    };

    const handleCopyTemplate = async () => {
        if (!user || !template) return;

        setCopying(true);
        try {
            // Create new project from template
            const { data: newProject, error: projectError } = await supabase
                .from('projects')
                .insert({
                    user_id: user.id,
                    name: `${template.name} (Copy)`,
                    screens: template.screens,
                    settings: template.settings || {}
                })
                .select()
                .single();

            if (projectError) throw projectError;

            // Track the template share
            await supabase
                .from('template_shares')
                .insert({
                    template_id: template.id,
                    copied_by_user_id: user.id,
                    project_id: newProject.id
                });

            toast.success('Template copied to your projects!');
            navigate(`/editor/${newProject.id}`);
        } catch (err) {
            console.error('Error copying template:', err);
            toast.error('Failed to copy template');
        } finally {
            setCopying(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4">
                <Card className="max-w-md w-full">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Lock className="h-5 w-5" />
                            Access Denied
                        </CardTitle>
                        <CardDescription>{error}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button onClick={() => navigate('/')} className="w-full">
                            Go to Dashboard
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-muted/30 p-4 md:p-8">
            <div className="max-w-4xl mx-auto space-y-6">
                <Card>
                    <CardHeader>
                        <div className="flex items-start justify-between">
                            <div>
                                <CardTitle className="text-2xl">{template.name}</CardTitle>
                                <CardDescription className="mt-2">
                                    {template.description || 'Shared template'}
                                </CardDescription>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Lock className="h-3 w-3" />
                                Private Template
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Template Preview */}
                        <div>
                            <h3 className="font-semibold mb-3">Template Preview</h3>
                            <div className="grid gap-3 md:grid-cols-3">
                                {template.screens?.slice(0, 3).map((screen, idx) => (
                                    <div
                                        key={idx}
                                        className="aspect-[9/16] bg-white rounded-lg border-2 border-border p-4 flex items-center justify-center text-sm text-muted-foreground"
                                    >
                                        Screen {idx + 1}
                                        <br />
                                        {screen.elements?.length || 0} elements
                                    </div>
                                ))}
                            </div>
                            {template.screens?.length > 3 && (
                                <p className="text-sm text-muted-foreground mt-2">
                                    +{template.screens.length - 3} more screens
                                </p>
                            )}
                        </div>

                        {/* Copy Button */}
                        <div className="flex gap-3">
                            <Button
                                onClick={handleCopyTemplate}
                                disabled={copying}
                                className="flex-1"
                                size="lg"
                            >
                                {copying ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Copying...
                                    </>
                                ) : (
                                    <>
                                        <Copy className="mr-2 h-4 w-4" />
                                        Copy Template to My Projects
                                    </>
                                )}
                            </Button>
                        </div>

                        <p className="text-xs text-muted-foreground text-center">
                            This will create a new project in your account with all screens and settings from this template.
                        </p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
